import { type FC, useEffect } from 'react';

import { type FlightData, type RoomData } from '@vakantiesnl/services/src/context/useAccommodationContext';
import { useAvailabilityCheckContext } from '@vakantiesnl/services/src/context/useAvailabilityCheckContext';
import { useMapGeo } from '@vakantiesnl/services/src/gtm/util/useMapGeo';
import { useGetAccommodationByEntityId } from '@vakantiesnl/services/src/hooks/queries';
import { getMicroCopy } from '@vakantiesnl/services/src/microcopy';
import { useGetFinalOffer } from '@vakantiesnl/services/src/stores/accommodationStore';
import { isAccoOnlyOffer } from '@vakantiesnl/services/src/util';
import { type MicroCopy } from '@vakantiesnl/types';
import dynamic from 'next/dynamic';

const InProgressContentModal = dynamic(
	() => import('./InProgressContentModal').then((module) => module.InProgressContentModal),
	{
		ssr: false,
	},
);

const TripAvailableContentModal = dynamic(
	() => import('./TripAvailableContentModal').then((module) => module.TripAvailableContentModal),
	{
		ssr: false,
	},
);

const UnavailableContentModal = dynamic(
	() => import('./UnavailableContentModal').then((module) => module.UnavailableContentModal),
	{
		ssr: false,
	},
);

type Props = {
	microCopy: MicroCopy;
	roomData?: RoomData;
	flightData?: FlightData;
};

export const AvailabilityCheckModal: FC<Props> = ({ microCopy, flightData, roomData }) => {
	const { state: availabilityState } = useAvailabilityCheckContext();
	const {
		available,
		checking,
		requiresNewPriceApproval,
		oldPrice,
		newPrice,
		flightAvailable,
		hotelAvailable,
		acceptedNewPrice,
	} = availabilityState;
	const { accommodation } = useGetAccommodationByEntityId();
	const currentOffer = useGetFinalOffer(accommodation);
	const tourOperator = isAccoOnlyOffer(currentOffer)
		? currentOffer.tourOperatorId.toString()
		: currentOffer?.tourOperator;

	const { country_name, region_name, city_name } = useMapGeo({
		country: accommodation.country,
		region: accommodation.region,
		city: accommodation.city,
	});

	useEffect(() => {
		if (!checking && available !== undefined && tourOperator && !acceptedNewPrice) {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) => {
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ availabilityCheck }) =>
					track(
						availabilityCheck({
							product_status: available ? 'Available' : 'Unavailable',
							request_price: oldPrice || 0,
							available_price: newPrice || 0,
							flight_status: available || Boolean(flightAvailable),
							hotel_status: available || Boolean(hotelAvailable),
							tour_operator: tourOperator,
							giata_id: accommodation.giataId,
							flight_data: flightData,
							room_data: roomData,
							acco_type: getMicroCopy(`accoType.${accommodation.type}`, microCopy),
							helios_id: accommodation.heliosId,
							geo: { country_name, region_name, city_name },
							...availabilityState.additionalGTMData,
						}),
					),
				);
			});
		}
	}, [
		acceptedNewPrice,
		available,
		checking,
		flightAvailable,
		hotelAvailable,
		microCopy,
		newPrice,
		oldPrice,
		tourOperator,
		country_name,
		region_name,
		city_name,
		availabilityState.additionalGTMData,
		accommodation.giataId,
		accommodation.type,
		accommodation.heliosId,
		flightData,
		roomData,
	]);

	if (checking) {
		return <InProgressContentModal />;
	}

	if (available === false) {
		return <UnavailableContentModal />;
	}

	return available || requiresNewPriceApproval ? <TripAvailableContentModal /> : null;
};
